import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { autoplannerAPI } from '../../../services/api';
import useExcludedRowers from '../../../hooks/useExcludedRowers';
import ExcludedRowersManagerDrawer from './validation/ExcludedRowersManagerDrawer';
import ValidationSummaryCard from './validation/ValidationSummaryCard';
import SeatCapacityCard from './validation/SeatCapacityCard';
import SchedulingFeasibilityCard from './validation/SchedulingFeasibilityCard';
import IssuesAndWarningsCard from './validation/IssuesAndWarningsCard';
import ResolutionsSection from '../validation/ResolutionsSection';
import SchedulingSection from '../validation/SchedulingSection';
import SeatCapacitySection from '../validation/SeatCapacitySection';
import SessionOverridesModal from './validation/SessionOverridesModal';
import useResolutions from '../../../hooks/useResolutions';
import useSessionOverrides from '../../../hooks/useSessionOverrides';
import useValidationExclusions from '../../../hooks/autoplanner/validation/useValidationExclusions';
import CompactStatusBadge from '../validation/CompactStatusBadge';
import AdvancedValidationPanel from '../validation/AdvancedValidationPanel';
import useValidationFlow from '../../../hooks/autoplanner/useValidationFlow';
import useCrewSessionOverrides from '../../../hooks/autoplanner/useCrewSessionOverrides';
import useDisclosure from '../../../hooks/ui/useDisclosure';
import useModal from '../../../hooks/ui/useModal';

const Step3Validation = ({
  trainingGroupId,
  selectedSessions,
  startDate,
  endDate,
  taskId,
  validationResult,
  processing,
  onComplete,
  onBack,
  onRetry,
  onSaveDraft,
  registerNav,
}) => {
  // Debug: Log the props received
  console.log('Step3Validation received props:', {
    trainingGroupId,
    selectedSessions: selectedSessions?.length || 0,
    startDate,
    endDate,
    validationResult: !!validationResult,
    processing,
  });
  // validation state handled by useValidationFlow
  const [userChoices, setUserChoices] = useState({
    assumeFullAvailability: false,
    ignoreMissingRowers: false,
    sendNotifications: false,
  });
  const [autoplannerTask, setAutoplannerTask] = useState(null);
  // session exclusions migrated to hook later in file
  const [resolutions, setResolutions] = useState({});
  const [resolutionStep, setResolutionStep] = useState('availability'); // 'availability', 'scheduling', 'complete'
  const [previousSessions, setPreviousSessions] = useState(null);
  // New state for per-day resolutions: { userId: { date: resolution } }
  const [perDayResolutions, setPerDayResolutions] = useState({});
  const [sentReminders, setSentReminders] = useState(new Set());
  const [notification, setNotification] = useState(null);
  const [isSavingResolutions, setIsSavingResolutions] = useState(false);
  // Session-level overrides are sourced from overridesHook and normalized below
  const [pendingRevalidationSessions, setPendingRevalidationSessions] = useState(new Set());
  const [isSavingOverridesBySession, setIsSavingOverridesBySession] = useState({});
  const overrideModal = useModal({ open: false });
  // Excluded rowers managed in drawer hook

  const [optimisticOverrideSessions, setOptimisticOverrideSessions] = useState(new Set());
  const autosaveTimerRef = useRef(null);
  // Bulk crew update loading state (will be provided by exclusions hook)
  // Excluded rowers manager drawer state + hook
  const excludedDrawer = useDisclosure(false);
  const excludedHook = useExcludedRowers(autoplannerTask?.id || taskId, {
    onSaved: () => {
      // fire a background revalidation when exclusions change
      triggerBackgroundValidation();
    },
  });
  // Tabs: guided vs advanced
  const [activeTab, setActiveTab] = useState('guided'); // 'guided' | 'advanced'
  // Drawers moved into sections
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);
  const resolutionsHook = useResolutions(autoplannerTask?.id || taskId, {
    onSaved: () => {
      // After resolution changes, run background validation
      triggerBackgroundValidation();
    },
  });
  const overridesHook = useSessionOverrides(autoplannerTask?.id || taskId, {
    onSaved: () => {
      triggerBackgroundValidation();
    },
  });
  const crewOverrides = useCrewSessionOverrides(overridesHook);
  const [initialDrawerSessions, setInitialDrawerSessions] = useState([]);
  const lastPrefetchedSessionsKeyRef = useRef('');

  // Derive a composite-key map for consumers that expect `${sessionId}-${crewId}` keys
  const sessionOverrides = useMemo(() => {
    const src = overridesHook.sessionOverrides || {};
    const converted = {};
    try {
      Object.entries(src).forEach(([key, value]) => {
        if (!value) return;
        // If key already a composite key (sessionId-crewId), keep as-is
        if (String(key).includes('-')) {
          converted[String(key)] = value;
          // If this entry is itself a group mapping composite keys, flatten them too
          if (value && typeof value === 'object') {
            Object.entries(value)
              .filter(([k]) => String(k).includes('-'))
              .forEach(([ck, ov]) => {
                converted[String(ck)] = ov;
              });
          }
          return;
        }
        // key is sessionId
        if (value && typeof value === 'object' && value.crew_id) {
          const compositeKey = `${key}-${value.crew_id}`;
          converted[String(compositeKey)] = value;
          return;
        }
        // value might be a grouped object with composite keys under this sessionId
        if (value && typeof value === 'object') {
          Object.entries(value)
            .filter(([k]) => String(k).includes('-'))
            .forEach(([ck, ov]) => {
              converted[String(ck)] = ov;
            });
          // Preserve the group under the session id for nested lookups
          converted[String(key)] = value;
        }
      });
    } catch {
      // ignore
    }
    return converted;
  }, [overridesHook.sessionOverrides]);

  // Utilities

  // Helper function to format dates consistently as yyyy-mm-dd strings
  const formatDateString = (date) => {
    if (typeof date === 'string') {
      // If it's already a string, ensure it's in yyyy-mm-dd format
      return date.split('T')[0];
    }
    // If it's a Date object, convert to yyyy-mm-dd
    return date.toISOString().split('T')[0];
  };

  // Helper function to format dates for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Exclusions hook (hydration + helpers)
  // Validation flow (centralized)
  const {
    validationData,
    setValidationData,
    isProcessing,
    progress,
    currentStep,
    backgroundValidating,
    lastValidationAt,
    validateNow,
    triggerBackgroundValidation,
  } = useValidationFlow({
    taskId,
    autoplannerTask,
    setAutoplannerTask,
    trainingGroupId,
    selectedSessions,
    startDate,
    endDate,
  });

  const exclusions = useValidationExclusions({
    autoplannerTask,
    setAutoplannerTask,
    taskId,
    triggerBackgroundValidation,
    notify: (n) => setNotification(n),
  });
  const isSessionExcluded = exclusions.isSessionExcluded;

  // Load existing resolutions and session exclusions when component mounts or validation data changes
  useEffect(() => {
    console.log('=== useEffect triggered for loading data ===');
    console.log('autoplannerTask exists:', !!autoplannerTask);
    console.log('validationData exists:', !!validationData);

    if (autoplannerTask && validationData) {
      console.log('Loading all data...');
      loadResolutions();
      exclusions.loadSessionExclusions();
      // Load all overrides for this task once, then prefetch any missing per-session entries if needed
      (async () => {
        try {
          await overridesHook.loadAllOverrides(autoplannerTask.id);
        } catch (e) {
          console.error('Failed loading all session overrides', e);
        }
      })();
      // Prefetch session overrides for relevant sessions (deduped in hook)
      try {
        const crews = validationData?.scheduling_feasibility?.crew_analysis || [];
        const ids = new Set();
        crews.forEach((crew) => {
          (crew.session_feasibility || []).forEach((sf) => {
            if (sf.session_id) ids.add(sf.session_id);
          });
        });
        const sessionIds = Array.from(ids).sort((a, b) => String(a).localeCompare(String(b)));
        const key = sessionIds.join(',');
        if (key && key !== lastPrefetchedSessionsKeyRef.current) {
          lastPrefetchedSessionsKeyRef.current = key;
          overridesHook.prefetchOverrides(sessionIds);
        }
      } catch (e) {
        console.error('Failed prefetching session overrides', e);
      }
      loadSentReminders();
      // excluded rowers are managed via drawer hook; no local load
    }
  }, [autoplannerTask, validationData]);

  // Cleanup notification on unmount
  useEffect(() => {
    return () => {
      if (notification) {
        setNotification(null);
      }
    };
  }, [notification]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  // Helper function to detect if sessions have changed
  const sessionsHaveChanged = (currentSessions, previousSessions) => {
    if (!previousSessions) return true;
    if (!currentSessions) return false;

    const currentIds = currentSessions.map((s) => s.id).sort();
    const previousIds = previousSessions.map((s) => s.id).sort();

    return JSON.stringify(currentIds) !== JSON.stringify(previousIds);
  };

  const loadResolutions = async () => {
    try {
      console.log('Loading resolutions for task:', autoplannerTask.id);
      const result = await autoplannerAPI.getResolutions(autoplannerTask.id);
      console.log('Resolutions API result:', result);
      if (result.success) {
        const backendResolutions = result.resolutions || {};
        console.log('Backend resolutions:', backendResolutions);
        setResolutions(backendResolutions);

        // Backend now stores per-day resolutions directly
        setPerDayResolutions(backendResolutions);
        console.log('Set per-day resolutions:', backendResolutions);
      }
    } catch (error) {
      console.error('Error loading resolutions:', error);
    }
  };

  // loadSessionExclusions handled in exclusions hook

  // loadSessionOverrides removed; overrides are fetched via overridesHook.prefetchOverrides

  const loadSentReminders = async () => {
    try {
      if (!autoplannerTask) return;

      const result = await autoplannerAPI.getSentReminders(autoplannerTask.id);
      if (result.success && result.sent_user_ids) {
        setSentReminders(new Set(result.sent_user_ids));
      }
    } catch (error) {
      console.error('Error loading sent reminders:', error);
    }
  };

  // Removed local excluded-rowers load/save; drawer hook owns persistence

  // Removed legacy startEditingExcludedRowers

  // Removed legacy in-component exclusion editing; ExcludedRowersManagerDrawer handles this

  // Clear all changes function
  const handleClearAllChanges = async () => {
    try {
      if (!autoplannerTask) return;

      // Clear all changes
      await Promise.all([
        // Clear resolutions by setting all to 'ignore'
        (async () => {
          const currentResolutions = resolutionsHook.perDayResolutions || {};
          const clearedResolutions = {};

          // Set all existing resolutions to 'ignore'
          Object.entries(currentResolutions).forEach(([userId, userResolutions]) => {
            const clearedUserResolutions = {};
            Object.keys(userResolutions || {}).forEach((date) => {
              clearedUserResolutions[date] = 'ignore';
            });
            if (Object.keys(clearedUserResolutions).length > 0) {
              clearedResolutions[userId] = clearedUserResolutions;
            }
          });

          await autoplannerAPI.storeResolutions(autoplannerTask.id, clearedResolutions);
          // Update local state immediately
          resolutionsHook.setPerDayResolutions(clearedResolutions);
        })(),
        // Clear session exclusions by including all sessions for each crew
        ...Object.entries(exclusions.sessionExclusions || {}).map(([crewId, excludedSessions]) =>
          autoplannerAPI.includeSessionsForCrew(autoplannerTask.id, crewId, excludedSessions)
        ),
        // Clear excluded rowers per session
        autoplannerAPI.saveExcludedRowersPerSession(autoplannerTask.id, {}),
        // Clear session overrides (clear each session)
        ...Object.keys(sessionOverrides || {})
          .map((key) => {
            // Extract session ID from composite key (sessionId-crewId)
            const parts = key.split('-');
            return parts[0]; // Return just the session ID
          })
          .filter((sessionId, index, arr) => arr.indexOf(sessionId) === index) // Remove duplicates
          .map((sessionId) => autoplannerAPI.clearSessionOverrides(autoplannerTask.id, sessionId)),
      ]);

      // Update local state immediately for immediate UI feedback
      exclusions.setSessionExclusions({});

      // Refresh all data from backend to ensure consistency
      await Promise.all([
        resolutionsHook.loadResolutions(),
        excludedHook.loadExcludedRowers(),
        overridesHook.prefetchOverrides([]),
        exclusions.refreshAutoplannerTask(),
      ]);

      // Show success notification
      setNotification({
        type: 'success',
        message: 'All changes have been cleared successfully.',
      });
      setTimeout(() => setNotification(null), 3000);

      // Trigger background validation to update the interface
      triggerBackgroundValidation();
    } catch (error) {
      console.error('Error clearing all changes:', error);
      setNotification({
        type: 'error',
        message: 'Failed to clear all changes. Please try again.',
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Open clear all changes confirmation modal
  const openClearAllModal = () => {
    setIsClearAllModalOpen(true);
  };

  // Close clear all changes confirmation modal
  const closeClearAllModal = () => {
    setIsClearAllModalOpen(false);
  };

  // Helper function to refresh autoplanner task data from backend
  const refreshAutoplannerTask = async () => {
    try {
      if (!autoplannerTask) return;

      console.log('Refreshing autoplanner task data for task ID:', autoplannerTask.id);
      const updatedTask = await autoplannerAPI.getAutoplannerTask(autoplannerTask.id);

      if (updatedTask) {
        console.log('Updated task data received:', {
          taskId: updatedTask.id,
          sessionExclusions: updatedTask.session_exclusions,
        });
        setAutoplannerTask(updatedTask);
        // Overrides are managed via overridesHook; no local hydration here
      }
    } catch (error) {
      console.error('Error refreshing autoplanner task:', error);
    }
  };

  // Compact validation status renderer
  const renderCompactStatus = () => {
    const isValid = validationData?.valid;
    return <CompactStatusBadge isValid={isValid} backgroundValidating={backgroundValidating} />;
  };

  // background validation handled via useValidationFlow

  // background validation is provided directly by useValidationFlow

  useEffect(() => {
    // Require a valid task id (or already loaded task) before attempting validation
    if (!taskId && !autoplannerTask) return;

    if (trainingGroupId && selectedSessions && startDate && endDate) {
      // Check if sessions have changed
      const sessionsChanged = sessionsHaveChanged(selectedSessions, previousSessions);

      if (sessionsChanged || !validationData) {
        // Reset state when sessions change or when first arriving without data
        setValidationData && setValidationData(null);
        setAutoplannerTask(null);
        exclusions.setSessionExclusions({});
        setResolutions({});
        setResolutionStep('availability');
        setUserChoices({
          assumeFullAvailability: false,
          ignoreMissingRowers: false,
          sendNotifications: false,
        });

        // Update previous sessions
        setPreviousSessions(selectedSessions);

        // Re-validate with new sessions
        validateNow();
      }
    }
  }, [trainingGroupId, selectedSessions, startDate, endDate, taskId]);

  const handleValidation = () => {
    validateNow();
  };

  const handleContinue = async () => {
    try {
      const currentTaskId = autoplannerTask?.id || taskId;
      if (!currentTaskId) {
        console.error('No autoplanner task ID found');
        return;
      }

      // Create normalized dataset before proceeding
      console.log('Creating normalized dataset for task:', currentTaskId);
      const result = await autoplannerAPI.createNormalizedDataset(currentTaskId);

      if (result.success) {
        console.log('Normalized dataset created successfully');
        // Pass validation data back to the parent component (task ID already known)
        onComplete(exclusions.sessionExclusions, resolutions);
      } else {
        console.error('Failed to create normalized dataset:', result.error);
        // Still proceed but log the error
        onComplete(exclusions.sessionExclusions, resolutions);
      }
    } catch (error) {
      console.error('Error creating normalized dataset:', error);
      // Still proceed but log the error
      onComplete(exclusions.sessionExclusions, resolutions);
    }
  };

  const handleBack = () => {
    onBack();
  };

  const handleRetry = () => {
    validateNow();
  };

  const handleExcludeSession = exclusions.excludeSession;

  const handleIncludeSession = exclusions.includeSession;

  // Bulk exclude/include all sessions for a crew
  const handleExcludeCrewAllSessions = exclusions.excludeCrewAllSessions;

  const handleIncludeCrewAllSessions = exclusions.includeCrewAllSessions;

  // ----- Session Overrides (background-first) -----
  const openAdjustAvailabilityModal = (crewId, session) => {
    overrideModal.openWith({ crewId, session });
    try {
      if (session?.session_id) {
        overridesHook.prefetchOverrides([session.session_id]);
      }
    } catch (e) {
      console.error('Prefetch on open failed', e);
    }
  };

  const closeAdjustAvailabilityModal = () => overrideModal.close();

  const handleSaveSessionOverrides = async (crewId, sessionId, payload) => {
    try {
      if (!autoplannerTask) return;
      setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: true }));
      // Optimistic local update via hook
      await overridesHook.saveSessionOverrides(sessionId, payload);
      const res = { success: true };
      if (res?.success) {
        setOptimisticOverrideSessions((prev) => new Set([...prev, sessionId]));
        setPendingRevalidationSessions((prev) => new Set([...prev, sessionId]));
        triggerBackgroundValidation();
        // Reload this session's overrides from backend (force fresh)
        try {
          await overridesHook.prefetchOverrides([sessionId], { force: true });
        } catch (err) {
          console.error('Prefetch after save failed', err);
        }
        closeAdjustAvailabilityModal();
      }
    } catch (e) {
      console.error('Failed saving session overrides', e);
    } finally {
      setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: false }));
    }
  };

  const hasOverrides = (sessionId) => {
    if (!sessionOverrides) return false;
    // Check for any crew-specific overrides for this session
    const hasAnyCrewOverride = Object.keys(sessionOverrides).some((key) => {
      if (key.startsWith(`${sessionId}-`)) {
        const entry = sessionOverrides[key];
        const mo = entry?.member_overrides;
        return mo && typeof mo === 'object' && Object.keys(mo).length > 0;
      }
      return false;
    });
    return hasAnyCrewOverride || optimisticOverrideSessions.has(sessionId);
  };

  const hasOverridesForCrew = (sessionId, crewId) => crewOverrides.hasOverridesForCrew(sessionId, crewId);

  const isRevalidatingSession = (sessionId) => pendingRevalidationSessions.has(sessionId);

  const getSessionOverride = (sessionId) => {
    if (!sessionOverrides) return null;
    // Find any crew-specific override for this session
    for (const key of Object.keys(sessionOverrides)) {
      if (key.startsWith(`${sessionId}-`)) {
        const entry = sessionOverrides[key];
        if (entry && entry.member_overrides && Object.keys(entry.member_overrides).length > 0) {
          return entry;
        }
      }
    }
    return null;
  };

  // Temporary flag to disable legacy advanced sections during refactor
  const showLegacyAdvanced = false;

  const getSessionOverrideForCrew = (sessionId, crewId) => {
    const compositeKey = `${sessionId}-${crewId}`;
    const local = sessionOverrides?.[compositeKey] || sessionOverrides?.[String(compositeKey)];
    if (local) return local;
    return crewOverrides.getSessionOverrideForCrew(sessionId, crewId);
  };

  // Build a normalized list of overrides for Activity tab rendering
  const getNormalizedOverridesList = () => {
    return crewOverrides.normalizedOverridesList || [];
  };

  const handleClearSessionOverride = async (sessionId, crewId) => {
    try {
      if (!autoplannerTask) return;
      setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: true }));
      let res;
      if (crewId) {
        res = await autoplannerAPI.clearSessionOverridesForCrew(
          autoplannerTask.id,
          sessionId,
          crewId
        );
      } else {
        res = await autoplannerAPI.clearSessionOverrides(autoplannerTask.id, sessionId);
      }
      if (res?.success) {
        setOptimisticOverrideSessions((prev) => {
          const next = new Set(prev);
          next.delete(sessionId);
          return next;
        });
        triggerBackgroundValidation();
        // Reload this session's overrides from backend
        try {
          await overridesHook.prefetchOverrides([sessionId], { force: true });
        } catch (err) {
          console.error('Prefetch after clear failed', err);
        }
      }
    } catch (e) {
      console.error('Failed clearing session override', e);
    } finally {
      setIsSavingOverridesBySession((s) => ({ ...s, [sessionId]: false }));
    }
  };

  const renderOverrideDetails = (sessionId, crewId) => {
    const ov = crewId
      ? getSessionOverrideForCrew(sessionId, crewId)
      : getSessionOverride(sessionId);
    if (!ov || !ov.member_overrides || Object.keys(ov.member_overrides).length === 0) return null;

    const memberCount = Object.keys(ov.member_overrides).length;
    const items = Object.entries(ov.member_overrides).map(([uid, cfg]) => {
      const info = getUserInfo(parseInt(uid, 10));
      const name = info ? info.name : `User ${uid}`;
      const firstRange = (cfg?.ranges && cfg.ranges[0]) || {};
      const start = firstRange.start || firstRange.start_time || '';
      const end = firstRange.end || firstRange.end_time || '';
      return { uid, name, start, end };
    });

    // Enhanced override information
    const crewInfo = {
      crewId: ov.crew_id,
      crewName: ov.crew_name || `Crew ${ov.crew_id}`,
      boatType: ov.boat_type || 'Unknown',
    };

    const timeInfo = {
      windowStart: ov.window_start,
      windowEnd: ov.window_end,
      duration: ov.duration_minutes,
    };

    const metadata = {
      createdAt: ov.created_at,
      originalIssue: ov.original_issue,
      totalMembersAffected: ov.total_members_affected || memberCount,
    };

    return (
      <div className="mt-2 rounded border border-blue-100 bg-blue-50 p-2">
        <div className="mb-1 text-[12px] font-medium text-blue-800">
          Override applied for this session ({memberCount} member{memberCount === 1 ? '' : 's'})
        </div>

        {/* Enhanced information display */}
        {timeInfo.windowStart && timeInfo.windowEnd && (
          <div className="mb-1 text-[11px] text-blue-700">
            Window: {timeInfo.windowStart} - {timeInfo.windowEnd} ({timeInfo.duration}min)
          </div>
        )}

        {crewInfo.crewName && (
          <div className="mb-1 text-[11px] text-blue-700">
            Crew: {crewInfo.crewName} ({crewInfo.boatType})
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <span
              key={it.uid}
              className="rounded bg-blue-100 px-2 py-0.5 text-[11px] text-blue-800"
            >
              {it.name} {it.start && it.end ? `(${it.start}-${it.end})` : ''}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const handleSendReminder = async (userId) => {
    try {
      if (!autoplannerTask) return;

      const result = await autoplannerAPI.sendAvailabilityReminder(autoplannerTask.id, userId);

      if (result.success) {
        // Add user to sent reminders set
        setSentReminders((prev) => new Set([...prev, userId]));

        // Show success notification
        setNotification({
          type: 'success',
          message: 'Availability reminder sent successfully!',
          userId,
        });

        // Clear notification after 3 seconds
        setTimeout(() => {
          setNotification(null);
        }, 3000);
      } else {
        // Show error notification
        setNotification({
          type: 'error',
          message: 'Failed to send reminder. Please try again.',
          userId,
        });

        // Clear notification after 5 seconds
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error sending reminder:', error);

      // Show error notification
      setNotification({
        type: 'error',
        message: 'Error sending reminder. Please try again.',
        userId,
      });

      // Clear notification after 5 seconds
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    }
  };

  // Removed apply button flow; resolutions save automatically with debounce

  // Helper function to get user information from validation data
  const getUserInfo = (userId) => {
    const availability_check = validationData?.availability_check;
    if (!availability_check) return null;

    const allUsers = [
      ...(availability_check.missing_users || []),
      ...(availability_check.missing_coaches || []),
      ...(availability_check.rowers_with_availability || []),
      ...(availability_check.coaches_with_availability || []),
    ];

    return allUsers.find((user) => user.user_id === userId);
  };

  const getResolutionData = () => {
    const rowerData = [];
    const coachData = [];
    const availability_check = validationData?.availability_check;

    // First, add users who already have resolutions
    Object.entries(perDayResolutions || {}).forEach(([userId, userResolutions]) => {
      // Only include users who have resolutions (empty strings count as resolutions)
      if (userResolutions && Object.keys(userResolutions).length > 0) {
        // Get all dates that have resolutions for this user and sort them
        const datesWithResolutions = Object.keys(userResolutions).sort((a, b) => {
          return new Date(a).getTime() - new Date(b).getTime();
        });
        const userInfo = getUserInfo(parseInt(userId));

        const userData = {
          user_id: parseInt(userId),
          resolutions: { ...userResolutions },
          dates: datesWithResolutions,
          userInfo,
        };

        // Determine if this is a rower or coach
        if (userInfo) {
          const isCoach =
            availability_check?.missing_coaches?.some((c) => c.user_id === parseInt(userId)) ||
            availability_check?.coaches_with_availability?.some(
              (c) => c.user_id === parseInt(userId)
            );

          if (isCoach) {
            coachData.push(userData);
          } else {
            rowerData.push(userData);
          }
        } else {
          // If we can't determine, assume it's a rower
          rowerData.push(userData);
        }
      }
    });

    // Then, add users with missing availability data who don't have resolutions yet
    if (availability_check) {
      // Add missing rowers
      (availability_check.missing_users || []).forEach((user) => {
        const userId = user.user_id;
        // Only add if not already in rowerData
        if (!rowerData.some((r) => r.user_id === userId)) {
          const userInfo = getUserInfo(userId);
          const missingDates = user.missing_dates || [];
          const userData = {
            user_id: userId,
            resolutions: {},
            dates: missingDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
            userInfo,
          };
          rowerData.push(userData);
        }
      });

      // Add missing coaches
      (availability_check.missing_coaches || []).forEach((coach) => {
        const userId = coach.user_id;
        // Only add if not already in coachData
        if (!coachData.some((c) => c.user_id === userId)) {
          const userInfo = getUserInfo(userId);
          const missingDates = coach.missing_dates || [];
          const userData = {
            user_id: userId,
            resolutions: {},
            dates: missingDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
            userInfo,
          };
          coachData.push(userData);
        }
      });
    }

    return {
      rowers: rowerData,
      coaches: coachData,
    };
  };

  // Debounced autosave of resolutions followed by background validate
  const queueAutosaveAndValidate = (resolutionsToSave) => {
    if (!autoplannerTask) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      try {
        setIsSavingResolutions(true);
        const result = await autoplannerAPI.storeResolutions(autoplannerTask.id, resolutionsToSave);
        if (result?.success) {
          setResolutions(resolutionsToSave);
          setPerDayResolutions(resolutionsToSave);
          triggerBackgroundValidation();
        }
      } catch (e) {
        console.error('Autosave resolutions failed:', e);
      } finally {
        setIsSavingResolutions(false);
      }
    }, 600);
  };

  // Helper function to parse detailed feedback
  const parseDetailedFeedback = (feedback) => {
    if (!feedback) return null;

    const parts = feedback.split(' | ');
    const result = {
      summary: '',
      fullAvailabilityMembers: [],
      timeSlots: [],
      bestOverlap: null,
    };

    parts.forEach((part) => {
      if (part.includes('members have availability but no overlapping time')) {
        result.summary = part;
      } else if (part.startsWith('Full availability members:')) {
        const membersStr = part.replace('Full availability members: ', '');
        result.fullAvailabilityMembers = membersStr
          .replace(/[[\]]/g, '')
          .split(',')
          .map((id) => parseInt(id.trim()));
      } else if (part.startsWith('Time slots:')) {
        const slotsStr = part.replace('Time slots: ', '');
        result.timeSlots = slotsStr
          .split('; ')
          .map((slot) => {
            const match = slot.match(/Member (\d+): (.+)/);
            if (match) {
              return {
                memberId: parseInt(match[1]),
                timeRanges: match[2].split(', '),
              };
            }
            return null;
          })
          .filter(Boolean);
      } else if (part.startsWith('Best available overlap:')) {
        const overlapStr = part.replace('Best available overlap: ', '');
        const match = overlapStr.match(/(\d+) members at (.+)/);
        if (match) {
          const timeMatch = match[2].match(/(.+) - (.+)/);
          if (timeMatch) {
            result.bestOverlap = {
              memberCount: parseInt(match[1]),
              startTime: timeMatch[1],
              endTime: timeMatch[2],
            };
          }
        }
      }
    });

    return result;
  };

  // Helper function to render detailed feedback
  const renderDetailedFeedback = (feedback, structuredFeedback = null) => {
    // If structured feedback is available, use it directly
    if (structuredFeedback) {
      return (
        <div className="space-y-3">
          {/* Full Availability Members */}
          {structuredFeedback.full_availability_members &&
            structuredFeedback.full_availability_members.length > 0 && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <div className="flex items-center space-x-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium text-green-800">
                    Full Availability Members
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {structuredFeedback.full_availability_members.map((memberId, index) => {
                    const userInfo = getUserInfo(memberId);
                    const displayName = userInfo
                      ? `${userInfo.name} (${userInfo.email})`
                      : `User ${memberId}`;
                    return (
                      <span
                        key={index}
                        className="inline-flex items-center rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
                      >
                        {displayName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Time Slots */}
          {structuredFeedback.time_slots && structuredFeedback.time_slots.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center space-x-2">
                <ClockIcon className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-800">
                  Member Availability Time Slots
                </span>
              </div>
              <div className="mt-2 space-y-2">
                {structuredFeedback.time_slots.map((slot, index) => {
                  const userInfo = getUserInfo(slot.member_id);
                  const displayName = userInfo ? `${userInfo.name}` : `User ${slot.member_id}`;
                  return (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-800">{displayName}</span>
                      <div className="flex flex-wrap gap-1">
                        {slot.time_ranges.map((timeRange, timeIndex) => (
                          <span
                            key={timeIndex}
                            className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800"
                          >
                            {timeRange.display}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Best Overlap */}
          {structuredFeedback.best_overlap && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                <span className="text-sm font-medium text-yellow-800">Best Available Overlap</span>
              </div>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  {structuredFeedback.best_overlap.member_count} members can be available together
                  from{' '}
                  <span className="font-medium">{structuredFeedback.best_overlap.start_time}</span>{' '}
                  to <span className="font-medium">{structuredFeedback.best_overlap.end_time}</span>
                </p>
                <p className="mt-1 text-xs text-yellow-600">
                  This is the best partial overlap available, but not enough for the full crew.
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Fallback to string parsing for backward compatibility
    const parsed = parseDetailedFeedback(feedback);
    if (!parsed) return <span className="text-sm text-gray-600">{feedback}</span>;

    return (
      <div className="space-y-3">
        {/* Summary */}
        <div className="text-sm text-gray-600">{parsed.summary}</div>

        {/* Full Availability Members */}
        {parsed.fullAvailabilityMembers.length > 0 && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium text-green-800">Full Availability Members</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {parsed.fullAvailabilityMembers.map((memberId, index) => {
                const userInfo = getUserInfo(memberId);
                const displayName = userInfo
                  ? `${userInfo.name} (${userInfo.email})`
                  : `User ${memberId}`;
                return (
                  <span
                    key={index}
                    className="inline-flex items-center rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
                  >
                    {displayName}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Time Slots */}
        {parsed.timeSlots.length > 0 && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-800">
                Member Availability Time Slots
              </span>
            </div>
            <div className="mt-2 space-y-2">
              {parsed.timeSlots.map((slot, index) => {
                const userInfo = getUserInfo(slot.memberId);
                const displayName = userInfo
                  ? `${userInfo.name} (${userInfo.email})`
                  : `User ${slot.memberId}`;
                return (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800">{displayName}</span>
                    <div className="flex flex-wrap gap-1">
                      {slot.timeRanges.map((timeRange, timeIndex) => (
                        <span
                          key={timeIndex}
                          className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800"
                        >
                          {timeRange}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Best Overlap */}
        {parsed.bestOverlap && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-800">Best Available Overlap</span>
            </div>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                {parsed.bestOverlap.memberCount} members can be available together from{' '}
                <span className="font-medium">{parsed.bestOverlap.startTime}</span> to{' '}
                <span className="font-medium">{parsed.bestOverlap.endTime}</span>
              </p>
              <p className="mt-1 text-xs text-yellow-600">
                This is the best partial overlap available, but not enough for the full crew.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (registerNav) {
      registerNav({
        onNext: () => (validationData && validationData.valid ? handleContinue() : handleRetry()),
        onBack: () => handleBack(),
        canNext: !isProcessing,
        canBack: !isProcessing,
      });
    }
  }, [registerNav, validationData, isProcessing]);

  if (isProcessing) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <h2 className="mt-4 text-lg font-medium text-gray-900">Validating Availability</h2>
          <p className="mt-2 text-sm text-gray-600">{currentStep}</p>
        </div>

        {/* Progress Bar */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Progress</span>
              <span className="text-sm text-gray-500">{progress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center space-x-3">
              <ClockIcon className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-sm text-gray-700">Checking rower availability...</span>
            </div>
            <div className="flex items-center space-x-3">
              <ClockIcon className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-sm text-gray-700">Validating scheduling feasibility...</span>
            </div>
            <div className="flex items-center space-x-3">
              <ClockIcon className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-sm text-gray-700">Analyzing seat capacity balance...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!validationData) {
    return (
      <div className="space-y-6 pb-24 sm:pb-0">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h2 className="mt-4 text-lg font-medium text-gray-900">Validation Required</h2>
          <p className="mt-2 text-sm text-gray-600">
            We need to validate the availability and scheduling feasibility before proceeding.
          </p>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="text-center">
            <p className="mb-4 text-sm text-gray-600">
              Click the button below to start the validation process.
            </p>
            <button
              onClick={handleRetry}
              className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Start Validation
            </button>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const {
    valid,
    availability_check,
    scheduling_feasibility,
    issues = [],
    warnings = [],
    recommendations = [],
  } = validationData || {};

  return (
    <div className="space-y-6 pb-24 sm:pb-0">
      {/* Desktop compact status (top-right, sticky) */}
      <div className="sticky top-0 z-20 hidden items-center justify-end gap-2 border-b border-gray-200 bg-white/80 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-white/60 sm:flex">
        {renderCompactStatus()}
      </div>
      {/* Notification */}
      {notification && (
        <div
          className={`fixed left-4 right-4 top-4 z-50 max-w-sm rounded-lg p-4 shadow-lg sm:left-auto sm:right-4 ${notification.type === 'success'
            ? 'border border-green-400 bg-green-100 text-green-700'
            : 'border border-red-400 bg-red-100 text-red-700'
            }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="mr-2 h-5 w-5 text-green-500" />
              ) : (
                <XCircleIcon className="mr-2 h-5 w-5 text-red-500" />
              )}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Summary + Tabs */}
      <div className="space-y-3">
        <ValidationSummaryCard
          validationData={validationData}
          lastValidatedAt={lastValidationAt}
          onResolve={() => setActiveTab('issues')}
        />
        <div className="rounded bg-white p-2 shadow sm:p-3">
          <div className="flex gap-2 overflow-x-auto">
            <button
              className={`flex-shrink-0 rounded px-3 py-1 text-sm ${activeTab === 'guided' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setActiveTab('guided')}
            >
              Guided
            </button>
            <button
              className={`flex-shrink-0 rounded px-3 py-1 text-sm ${activeTab === 'issues' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setActiveTab('issues')}
            >
              Issues
            </button>
            <button
              className={`flex-shrink-0 rounded px-3 py-1 text-sm ${activeTab === 'warnings' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setActiveTab('warnings')}
            >
              Warnings
            </button>
            <button
              className={`flex-shrink-0 rounded px-3 py-1 text-sm ${activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setActiveTab('activity')}
            >
              Activity
            </button>
            <button
              className={`ml-auto hidden flex-shrink-0 rounded px-3 py-1 text-sm sm:block ${activeTab === 'advanced' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced
            </button>
          </div>
        </div>
      </div>

      {/* Guided Overview */}
      {activeTab === 'guided' && (
        <div className="space-y-4">
          {/* Step 0: Issues & Warnings Summary - Always show first */}
          <div className="rounded-lg bg-white p-4 shadow sm:p-6">
            <div className="mb-3">
              <h3 className="text-lg font-medium text-gray-900">Validation Summary</h3>
              <p className="text-sm text-gray-600">
                Review the issues below and follow the steps to resolve them.
              </p>
            </div>

            {/* Issues */}
            {issues && issues.length > 0 && (
              <div className="mb-3">
                <div className="mb-2 flex items-center gap-2">
                  <XCircleIcon className="h-5 w-5 text-red-500" />
                  <span className="text-sm font-medium text-red-700">
                    {issues.length} Issue{issues.length === 1 ? '' : 's'} Found
                  </span>
                </div>
                <ul className="space-y-1">
                  {issues.map((issue, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-red-600">
                      <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500"></span>
                      <span>{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {warnings && warnings.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-700">
                    {warnings.length} Warning{warnings.length === 1 ? '' : 's'}
                  </span>
                </div>
                <ul className="space-y-1">
                  {warnings.map((warning, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-yellow-600">
                      <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-yellow-500"></span>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No issues */}
            {(!issues || issues.length === 0) && (!warnings || warnings.length === 0) && (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium">All validation checks passed!</span>
              </div>
            )}
          </div>

          <ResolutionsSection
            validationData={validationData}
            resolutionsHook={resolutionsHook}
            getResolutionData={getResolutionData}
            onSendReminder={handleSendReminder}
            sentReminders={sentReminders}
            getUserInfo={getUserInfo}
          />

          <SchedulingSection
            validationData={validationData}
            isSessionExcluded={isSessionExcluded}
            handleExcludeSession={handleExcludeSession}
            handleIncludeSession={handleIncludeSession}
            handleExcludeCrewAllSessions={handleExcludeCrewAllSessions}
            handleIncludeCrewAllSessions={handleIncludeCrewAllSessions}
            openAdjustAvailabilityModal={openAdjustAvailabilityModal}
            isUpdatingCrew={exclusions.isUpdatingCrew}
            formatDate={formatDate}
            sessionOverrides={sessionOverrides}
            handleClearSessionOverride={handleClearSessionOverride}
            getUserInfo={getUserInfo}
          />

          <SeatCapacitySection
            validationData={validationData}
            onOpenDrawer={() => excludedDrawer.open()}
          />

          {/* Progress Summary */}
          {(() => {
            const totalSteps = 3;
            const completedSteps = [
              // Step 1: Availability
              (() => {
                const missingUsers = validationData?.availability_check?.missing_users || [];
                const ignoredUsers = [];
                Object.entries(resolutionsHook.perDayResolutions || {}).forEach(
                  ([userId, userResolutions]) => {
                    const hasIgnoredResolutions = Object.values(userResolutions || {}).some(
                      (resolution) => resolution === 'ignore'
                    );
                    if (hasIgnoredResolutions) {
                      ignoredUsers.push(parseInt(userId));
                    }
                  }
                );
                const totalMissingUsers = missingUsers.length + ignoredUsers.length;
                return totalMissingUsers === 0;
              })(),
              // Step 2: Scheduling
              !validationData?.scheduling_feasibility?.crew_analysis ||
              validationData.scheduling_feasibility.crew_analysis.filter((crew) => {
                const hasNonFeasibleSessions = (crew.session_feasibility || []).some(
                  (sf) => !sf.feasible && !sf.excluded
                );
                const hasExcludedSessions = (crew.session_feasibility || []).some(
                  (sf) => sf.excluded
                );

                // Consider it resolved if there are no non-feasible sessions
                // Session overrides and excluded sessions are considered valid fixes
                return hasNonFeasibleSessions;
              }).length === 0,
              // Step 3: Seat Capacity
              !validationData?.seat_capacity_balance?.session_analysis ||
              validationData.seat_capacity_balance.session_analysis.filter(
                (s) => s.issue_type === 'insufficient_rowers' || s.issue_type === 'excess_rowers'
              ).length === 0,
            ].filter(Boolean).length;

            if (completedSteps < totalSteps) {
              return (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-2 flex items-center gap-3">
                    <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium text-blue-900">Progress</span>
                  </div>
                  <div className="ml-8">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-blue-200">
                        <div
                          className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-blue-700">
                        {completedSteps}/{totalSteps} steps complete
                      </span>
                    </div>
                    <p className="text-sm text-blue-700">
                      Complete the steps above to resolve all validation issues.
                    </p>
                  </div>
                </div>
              );
            } else {
              return (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    <div>
                      <span className="text-sm font-medium text-green-900">
                        All Steps Complete!
                      </span>
                      <p className="text-sm text-green-700">
                        You can now proceed to the next step.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
          })()}
        </div>
      )}

      {/* Issues tab: prioritized list with Fix actions (availability → scheduling → seat capacity) */}
      {activeTab === 'issues' && (
        <div className="space-y-3">
          {/* Seat capacity: insufficient and excess rowers */}
          {validationData?.seat_capacity_balance?.session_analysis &&
            (validationData.seat_capacity_balance.session_analysis.filter(
              (s) => s.issue_type === 'insufficient_rowers'
            ).length > 0 ||
              validationData.seat_capacity_balance.session_analysis.filter(
                (s) => s.issue_type === 'excess_rowers'
              ).length > 0) && (
              <div className="rounded-lg bg-white p-4 shadow sm:p-6">
                <div className="mb-2 text-sm font-medium text-gray-900">Seat capacity issues</div>
                <div className="space-y-2">
                  {validationData.seat_capacity_balance.session_analysis
                    .filter(
                      (s) =>
                        s.issue_type === 'insufficient_rowers' || s.issue_type === 'excess_rowers'
                    )
                    .map((s) => (
                      <div
                        key={s.session_id}
                        className="flex flex-col gap-2 rounded border p-2 text-xs sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900">
                            {s.session_title || `Session ${s.session_id}`}
                          </div>
                          <div className="mt-0.5 text-gray-600">
                            {formatDate(s.session_date)} • Seats {s.total_seats_needed} • Available{' '}
                            {s.available_rowers_count} • Balance {s.balance_diff}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setInitialDrawerSessions([s.session_id]);
                            excludedDrawer.open();
                          }}
                          className="w-full rounded bg-blue-600 px-2.5 py-1 text-white hover:bg-blue-700 sm:ml-2 sm:w-auto"
                        >
                          Fix
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

          {/* Scheduling infeasible sessions */}
          {validationData?.scheduling_feasibility?.crew_analysis && (
            <div className="rounded-lg bg-white p-4 shadow sm:p-6">
              <div className="mb-2 text-sm font-medium text-gray-900">Scheduling issues</div>
              <div className="space-y-2">
                {(() => {
                  const crews = validationData.scheduling_feasibility.crew_analysis || [];
                  const crewsWithIssues = crews.filter((crew) => {
                    const hasNonFeasibleSessions = (crew.session_feasibility || []).some(
                      (sf) => !sf.feasible && !sf.excluded
                    );
                    const hasExcludedSessions = (crew.session_feasibility || []).some(
                      (sf) => sf.excluded
                    );

                    // Consider it resolved if there are no non-feasible sessions
                    // Session overrides and excluded sessions are considered valid fixes
                    return hasNonFeasibleSessions;
                  });

                  if (crewsWithIssues.length === 0) {
                    return <div className="text-xs text-gray-500">No scheduling issues.</div>;
                  }
                  return crewsWithIssues.map((crew) => {
                    const nonFeasible = (crew.session_feasibility || []).filter(
                      (sf) => !sf.feasible
                    );
                    const crewSessionIds = (crew.session_feasibility || [])
                      .map((s) => s.session_id)
                      .filter(Boolean);
                    const loading = Boolean(exclusions.isUpdatingCrew[crew.crew_id]);
                    const isFullyExcluded =
                      crewSessionIds.length > 0 &&
                      crewSessionIds.every((sid) => isSessionExcluded(crew.crew_id, sid));
                    return (
                      <div key={crew.crew_id} className="rounded border p-2">
                        <div className="mb-1 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900">
                              {crew.crew_name} ({crew.boat_type})
                            </div>
                            <div className="text-[11px] text-gray-600">
                              {nonFeasible.length} non-feasible session
                              {nonFeasible.length === 1 ? '' : 's'}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {loading && (
                              <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">
                                Updating…
                              </span>
                            )}
                            {!loading &&
                              (isFullyExcluded ? (
                                <button
                                  onClick={() =>
                                    handleIncludeCrewAllSessions(crew.crew_id, crewSessionIds)
                                  }
                                  className="rounded bg-gray-600 px-2 py-1 text-xs text-white hover:bg-gray-700"
                                  title="Include this crew for all sessions"
                                >
                                  Include Crew
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleExcludeCrewAllSessions(crew.crew_id, crewSessionIds)
                                  }
                                  className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                                  title="Exclude this crew for all sessions"
                                >
                                  Exclude Crew
                                </button>
                              ))}
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          {nonFeasible.map((sf, idx) => {
                            const excluded = isSessionExcluded(crew.crew_id, sf.session_id);
                            return (
                              <div
                                key={`${crew.crew_id}-${sf.session_id}-${idx}`}
                                className="flex flex-col gap-2 rounded border p-2 text-xs sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900">
                                    {formatDate(sf.session_date)}
                                  </div>
                                  <div
                                    className={`mt-0.5 ${excluded ? 'text-gray-600' : 'text-red-600'}`}
                                  >
                                    {sf.reason}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5 sm:ml-2">
                                  {!excluded ? (
                                    <>
                                      <button
                                        onClick={() =>
                                          openAdjustAvailabilityModal(crew.crew_id, sf)
                                        }
                                        className="rounded bg-blue-600 px-2.5 py-1 text-white hover:bg-blue-700"
                                        title="Adjust availability"
                                      >
                                        Fix
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleExcludeSession(crew.crew_id, sf.session_id)
                                        }
                                        className="rounded border border-red-300 bg-white px-2.5 py-1 text-red-600 hover:bg-red-50"
                                        title="Exclude this session from scheduling"
                                      >
                                        Exclude
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">
                                        Excluded
                                      </span>
                                      <button
                                        onClick={() =>
                                          handleIncludeSession(crew.crew_id, sf.session_id)
                                        }
                                        className="rounded border border-gray-300 bg-white px-2.5 py-1 text-gray-700 hover:bg-gray-50"
                                        title="Include this session"
                                      >
                                        Include
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* Missing availability issues (rowers) */}
          {validationData?.availability_check && (
            <div className="rounded-lg bg-white p-4 shadow sm:p-6">
              <div className="mb-2 text-sm font-medium text-gray-900">Availability issues</div>
              <div className="flex flex-col gap-2 rounded border p-2 text-xs sm:flex-row sm:items-center sm:justify-between">
                <div className="text-gray-700">
                  {validationData.availability_check?.missing_users?.length || 0} rower(s) missing
                  availability
                </div>
                <button
                  onClick={() => setIsResolutionDrawerOpen(true)}
                  className="w-full rounded bg-blue-600 px-2.5 py-1 text-white hover:bg-blue-700 sm:ml-2 sm:w-auto"
                >
                  Fix
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Warnings tab */}
      {activeTab === 'warnings' && (
        <div className="space-y-3">
          {validationData?.availability_check?.missing_coaches &&
            validationData.availability_check.missing_coaches.length > 0 && (
              <div className="rounded-lg bg-white p-4 shadow sm:p-6">
                <div className="mb-2 text-sm font-medium text-gray-900">
                  Coach availability warnings
                </div>
                <div className="text-xs text-yellow-700">
                  {validationData.availability_check.missing_coaches.length} coach(es) missing
                  availability (auto-ignored unless explicitly included)
                </div>
              </div>
            )}
        </div>
      )}

      {/* Activity tab */}
      {activeTab === 'activity' && (
        <div className="space-y-4">
          {/* Summary and Clear All Button */}
          <div className="rounded-lg bg-white p-4 shadow sm:p-6">
            <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Applied Changes</h3>
                <p className="text-sm text-gray-600">
                  Review and manage all changes made during validation
                </p>
              </div>
              {(() => {
                const hasChanges =
                  Object.keys(excludedHook.excludedRowersPerSession || {}).length > 0 ||
                  Object.keys(sessionOverrides || {}).length > 0 ||
                  Object.keys(resolutionsHook.perDayResolutions || {}).length > 0 ||
                  Object.keys(exclusions.sessionExclusions || {}).length > 0;

                if (hasChanges) {
                  return (
                    <button
                      onClick={openClearAllModal}
                      className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                    >
                      Clear All Changes
                    </button>
                  );
                }
                return null;
              })()}
            </div>

            {/* Changes Summary */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Availability Resolutions */}
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900">Availability Resolutions</h4>
                  <span className="text-xs text-gray-500">
                    {Object.keys(resolutionsHook.perDayResolutions || {}).length} users
                  </span>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  {Object.keys(resolutionsHook.perDayResolutions || {}).length === 0 ? (
                    <div className="text-gray-400">No resolutions applied</div>
                  ) : (
                    Object.entries(resolutionsHook.perDayResolutions || {}).map(
                      ([userId, userResolutions]) => {
                        const userInfo = getUserInfo(parseInt(userId));
                        const userName = userInfo ? userInfo.name : `User ${userId}`;
                        const resolutionCount = Object.keys(userResolutions || {}).length;
                        return (
                          <div key={userId} className="flex items-center justify-between">
                            <span>{userName}</span>
                            <span className="text-blue-600">
                              {resolutionCount} date{resolutionCount === 1 ? '' : 's'}
                            </span>
                          </div>
                        );
                      }
                    )
                  )}
                </div>
              </div>

              {/* Session Exclusions */}
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900">Session Exclusions</h4>
                  <span className="text-xs text-gray-500">
                    {Object.keys(exclusions.sessionExclusions || {}).length} crews
                  </span>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  {Object.keys(exclusions.sessionExclusions || {}).length === 0 ? (
                    <div className="text-gray-400">No sessions excluded</div>
                  ) : (
                    Object.entries(exclusions.sessionExclusions || {}).map(([crewId, excludedSessions]) => {
                      const crewName = `Crew ${crewId}`;
                      return (
                        <div key={crewId} className="flex items-center justify-between">
                          <span>{crewName}</span>
                          <span className="text-red-600">
                            {excludedSessions.length} session
                            {excludedSessions.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Excluded Rowers */}
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900">Excluded Rowers</h4>
                  <span className="text-xs text-gray-500">
                    {Object.keys(excludedHook.excludedRowersPerSession || {}).length} sessions
                  </span>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  {Object.keys(excludedHook.excludedRowersPerSession || {}).length === 0 ? (
                    <div className="text-gray-400">No rowers excluded</div>
                  ) : (
                    Object.entries(excludedHook.excludedRowersPerSession || {}).map(
                      ([sessionId, excludedRowers]) => {
                        return (
                          <div key={sessionId} className="flex items-center justify-between">
                            <span>Session {sessionId}</span>
                            <span className="text-yellow-600">
                              {excludedRowers.length} rower{excludedRowers.length === 1 ? '' : 's'}
                            </span>
                          </div>
                        );
                      }
                    )
                  )}
                </div>
              </div>

              {/* Session Overrides */}
              <div className="rounded-lg border border-gray-200 p-3">
                {(() => {
                  const normalized = getNormalizedOverridesList();
                  return (
                    <>
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">Session Overrides</h4>
                        <span className="text-xs text-gray-500">{normalized.length} sessions</span>
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        {normalized.length === 0 ? (
                          <div className="text-gray-400">No overrides applied</div>
                        ) : (
                          normalized.map(({ sessionId, crewId, override }) => {
                            const memberCount = Object.keys(override?.member_overrides || {}).length;
                            return (
                              <div key={`${sessionId}-${crewId}`} className="flex items-center justify-between">
                                <span>
                                  Session {sessionId} • Crew {crewId}
                                </span>
                                <span className="text-green-600">
                                  {memberCount} member{memberCount === 1 ? '' : 's'}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* No Changes Message */}
            {(() => {
              const hasAnyChanges =
                Object.keys(excludedHook.excludedRowersPerSession || {}).length > 0 ||
                Object.keys(sessionOverrides || {}).length > 0 ||
                Object.keys(resolutionsHook.perDayResolutions || {}).length > 0 ||
                Object.keys(exclusions.sessionExclusions || {}).length > 0;

              if (!hasAnyChanges) {
                return (
                  <div className="mt-4 py-6 text-center">
                    <CheckCircleIcon className="mx-auto h-8 w-8 text-green-500" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No Changes Applied</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      All validation settings are at their default state.
                    </p>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Detailed Changes Sections */}
          {(() => {
            const hasAnyChanges =
              Object.keys(excludedHook.excludedRowersPerSession || {}).length > 0 ||
              Object.keys(sessionOverrides || {}).length > 0 ||
              Object.keys(resolutionsHook.perDayResolutions || {}).length > 0 ||
              Object.keys(exclusions.sessionExclusions || {}).length > 0;

            if (!hasAnyChanges) return null;

            return (
              <div className="space-y-4">
                {/* Detailed Availability Resolutions */}
                {Object.keys(resolutionsHook.perDayResolutions || {}).length > 0 && (
                  <div className="rounded-lg bg-white p-4 shadow sm:p-6">
                    <h4 className="mb-4 text-lg font-medium text-gray-900">
                      Availability Resolutions
                    </h4>
                    <div className="space-y-4">
                      {Object.entries(resolutionsHook.perDayResolutions || {}).map(
                        ([userId, userResolutions]) => {
                          const userInfo = getUserInfo(parseInt(userId));
                          const userName = userInfo ? userInfo.name : `User ${userId}`;
                          const userEmail = userInfo ? userInfo.email : '';

                          return (
                            <div key={userId} className="rounded-lg border border-gray-200 p-3">
                              <div className="mb-2 flex items-center justify-between">
                                <div>
                                  <h5 className="text-sm font-medium text-gray-900">{userName}</h5>
                                  {userEmail && (
                                    <p className="text-xs text-gray-500">{userEmail}</p>
                                  )}
                                </div>
                                <span className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-600">
                                  {Object.keys(userResolutions || {}).length} resolution
                                  {Object.keys(userResolutions || {}).length === 1 ? '' : 's'}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {Object.entries(userResolutions || {}).map(([date, resolution]) => (
                                  <div
                                    key={date}
                                    className="flex items-center justify-between text-xs"
                                  >
                                    <span className="text-gray-600">{date}</span>
                                    <span
                                      className={`rounded px-2 py-1 text-xs font-medium ${resolution === 'assume_available'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                        }`}
                                    >
                                      {resolution === 'assume_available'
                                        ? 'Assume Available'
                                        : 'Ignore'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

                {/* Detailed Session Exclusions */}
                {Object.keys(exclusions.sessionExclusions || {}).length > 0 && (
                  <div className="rounded-lg bg-white p-4 shadow sm:p-6">
                    <h4 className="mb-4 text-lg font-medium text-gray-900">Session Exclusions</h4>
                    <div className="space-y-4">
                      {Object.entries(exclusions.sessionExclusions || {}).map(([crewId, excludedSessions]) => {
                        const crewName = `Crew ${crewId}`;

                        return (
                          <div key={crewId} className="rounded-lg border border-gray-200 p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <h5 className="text-sm font-medium text-gray-900">{crewName}</h5>
                              <span className="rounded bg-red-50 px-2 py-1 text-xs text-red-600">
                                {excludedSessions.length} session
                                {excludedSessions.length === 1 ? '' : 's'} excluded
                              </span>
                            </div>
                            <div className="space-y-1">
                              {excludedSessions.map((sessionId) => (
                                <div
                                  key={sessionId}
                                  className="flex items-center justify-between text-xs"
                                >
                                  <span className="text-gray-600">Session {sessionId}</span>
                                  <span className="text-red-600">Excluded</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Detailed Excluded Rowers */}
                {Object.keys(excludedHook.excludedRowersPerSession || {}).length > 0 && (
                  <div className="rounded-lg bg-white p-4 shadow sm:p-6">
                    <h4 className="mb-4 text-lg font-medium text-gray-900">Excluded Rowers</h4>
                    <div className="space-y-4">
                      {Object.entries(excludedHook.excludedRowersPerSession || {}).map(
                        ([sessionId, excludedRowers]) => (
                          <div key={sessionId} className="rounded-lg border border-gray-200 p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <h5 className="text-sm font-medium text-gray-900">
                                Session {sessionId}
                              </h5>
                              <span className="rounded bg-yellow-50 px-2 py-1 text-xs text-yellow-600">
                                {excludedRowers.length} rower
                                {excludedRowers.length === 1 ? '' : 's'} excluded
                              </span>
                            </div>
                            <div className="space-y-1">
                              {excludedRowers.map((exclusion) => {
                                const userInfo = getUserInfo(exclusion.user_id);
                                const userName = userInfo
                                  ? userInfo.name
                                  : `User ${exclusion.user_id}`;

                                return (
                                  <div
                                    key={exclusion.user_id}
                                    className="flex items-center justify-between text-xs"
                                  >
                                    <span className="text-gray-600">{userName}</span>
                                    <span className="text-yellow-600">Excluded</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Detailed Session Overrides */}
                {Object.keys(sessionOverrides || {}).length > 0 && (
                  <div className="rounded-lg bg-white p-4 shadow sm:p-6">
                    <h4 className="mb-4 text-lg font-medium text-gray-900">Session Overrides</h4>
                    <div className="space-y-4">
                      {Object.entries(sessionOverrides || {}).map(([compositeKey, override]) => {
                        // Parse composite key (sessionId-crewId)
                        const [sessionId, crewId] = compositeKey.split('-');
                        const memberOverrides = override?.member_overrides || {};
                        const memberCount = Object.keys(memberOverrides).length;

                        return (
                          <div key={compositeKey} className="rounded-lg border border-gray-200 p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <div>
                                <h5 className="text-sm font-medium text-gray-900">
                                  Session {sessionId} • Crew {crewId}
                                </h5>
                                {override?.crew_name && (
                                  <p className="text-xs text-gray-500">{override.crew_name}</p>
                                )}
                              </div>
                              <span className="rounded bg-green-50 px-2 py-1 text-xs text-green-600">
                                {memberCount} member{memberCount === 1 ? '' : 's'} overridden
                              </span>
                            </div>

                            {/* Override details */}
                            <div className="space-y-2">
                              {Object.entries(memberOverrides).map(([userId, memberOverride]) => {
                                const userInfo = getUserInfo(parseInt(userId));
                                const userName = userInfo ? userInfo.name : `User ${userId}`;
                                const overrideType = memberOverride?.type || 'unknown';

                                return (
                                  <div key={userId} className="border-l-2 border-green-200 pl-3">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-gray-600">{userName}</span>
                                      <span
                                        className={`rounded px-2 py-1 text-xs font-medium ${overrideType === 'anytime'
                                          ? 'bg-green-100 text-green-800'
                                          : overrideType === 'unavailable'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-blue-100 text-blue-800'
                                          }`}
                                      >
                                        {overrideType === 'anytime'
                                          ? 'Available All Day'
                                          : overrideType === 'unavailable'
                                            ? 'Unavailable'
                                            : overrideType === 'custom_ranges'
                                              ? 'Custom Times'
                                              : overrideType}
                                      </span>
                                    </div>

                                    {/* Show time ranges if available */}
                                    {memberOverride?.ranges && memberOverride.ranges.length > 0 && (
                                      <div className="mt-1 space-y-1">
                                        {memberOverride.ranges.map((range, index) => (
                                          <div key={index} className="text-xs text-gray-500">
                                            {range.start || range.start_time} -{' '}
                                            {range.end || range.end_time}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Session-level overrides */}
                            {(override?.duration_minutes || override?.global_constraints) && (
                              <div className="mt-3 border-t border-gray-100 pt-3">
                                <h6 className="mb-1 text-xs font-medium text-gray-700">
                                  Session Settings
                                </h6>
                                <div className="space-y-1 text-xs text-gray-600">
                                  {override.duration_minutes && (
                                    <div>Duration: {override.duration_minutes} minutes</div>
                                  )}
                                  {override.global_constraints && (
                                    <div>Global constraints applied</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === 'advanced' && (
        <AdvancedValidationPanel
          validationData={validationData}
          getUserInfo={getUserInfo}
          formatDate={formatDate}
          isSessionExcluded={isSessionExcluded}
          handleExcludeSession={handleExcludeSession}
          handleIncludeSession={handleIncludeSession}
          handleExcludeCrewAllSessions={handleExcludeCrewAllSessions}
          handleIncludeCrewAllSessions={handleIncludeCrewAllSessions}
          hasOverridesForCrew={hasOverridesForCrew}
          isSavingOverridesBySession={isSavingOverridesBySession}
          isUpdatingCrew={exclusions.isUpdatingCrew}
          isRevalidatingSession={isRevalidatingSession}
          openAdjustAvailabilityModal={openAdjustAvailabilityModal}
          handleClearSessionOverride={handleClearSessionOverride}
        />
      )}

      {/* Success Message */}
      {valid && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 sm:p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">All Checks Passed</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  The validation was successful. All rowers have provided their availability and
                  scheduling is feasible. You can proceed to the next step.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col items-center gap-2 md:hidden">
        {renderCompactStatus()}
        {!backgroundValidating && issues && issues.length > 0 && (
          <button
            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
            onClick={() => setActiveTab('issues')}
            title="Go to issues"
          >
            Resolve {issues.length} issue{issues.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
        >
          Back
        </button>

        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          {valid ? (
            <button
              type="button"
              onClick={handleContinue}
              className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:w-auto"
            >
              Retry Validation
            </button>
          )}
        </div>
      </div>
      <ExcludedRowersManagerDrawer
        open={excludedDrawer.isOpen}
        onClose={excludedDrawer.close}
        taskId={autoplannerTask?.id || taskId}
        seatCapacityBalance={validationData?.seat_capacity_balance}
        getUserInfo={getUserInfo}
        hook={{
          excludedRowersPerSession: excludedHook.excludedRowersPerSession,
          savingExcludedRowers: excludedHook.savingExcludedRowers,
          addExcludedRower: excludedHook.addExcludedRower,
          removeExcludedRower: excludedHook.removeExcludedRower,
          updateExcludedRowerReason: excludedHook.updateExcludedRowerReason,
          bulkExcludeRower: excludedHook.bulkExcludeRower,
          bulkIncludeRower: excludedHook.bulkIncludeRower,
        }}
        initialSelectedSessionIds={initialDrawerSessions}
      />
      {/* Resolution Drawer */}
      {/* Moved ResolutionOptionsDrawer into ResolutionsSection */}

      {/* Session Overrides Modal */}
      <SessionOverridesModal
        open={overrideModal.state.open}
        onClose={closeAdjustAvailabilityModal}
        session={overrideModal.state.payload?.session}
        crewId={overrideModal.state.payload?.crewId}
        crew={(() => {
          try {
            const crews = validationData?.scheduling_feasibility?.crew_analysis || [];
            return crews.find((c) => c.crew_id === overrideModal.state.payload?.crewId) || null;
          } catch {
            return null;
          }
        })()}
        assumedAvailableUserIdsForDate={(() => {
          try {
            const dateStr = (overrideModal.state.payload?.session?.session_date || '').split('T')[0];
            const ids = Object.entries(perDayResolutions || {})
              .filter(([_, dates]) => dates && dates[dateStr] === 'assume_available')
              .map(([uid]) => parseInt(uid, 10))
              .filter(Boolean);
            return ids;
          } catch {
            return [];
          }
        })()}
        existingSessionOverride={getSessionOverrideForCrew(
          overrideModal.state.payload?.session?.session_id,
          overrideModal.state.payload?.crewId
        )}
        getUserInfo={getUserInfo}
        onSave={handleSaveSessionOverrides}
        isSaving={isSavingOverridesBySession[overrideModal.state.payload?.session?.session_id]}
      />

      {/* Scheduling Issues Drawer */}
      {/* Moved SchedulingIssuesDrawer into SchedulingSection */}

      {/* Clear All Changes Confirmation Modal */}
      {isClearAllModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeClearAllModal}
            ></div>
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    Clear All Changes
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to clear all changes? This will revert all availability
                      resolutions, session exclusions, excluded rowers, and session overrides to
                      their default state.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 gap-3 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  onClick={() => {
                    closeClearAllModal();
                    handleClearAllChanges();
                  }}
                >
                  Clear All Changes
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={closeClearAllModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step3Validation;
